import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment } from './arduino-generator.js';
import { ensureI2sAudioBus } from './beginner-i2s-audio.js';

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function clampVolume(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 80;
  }
  return Math.min(100, Math.max(0, Math.round(numeric)));
}

function speakerHelperCode(): string {
  return [
    'int skyroverSpeakerVolume = 80;',
    'volatile bool skyroverSpeakerStopRequested = false;',
    'volatile bool skyroverSpeakerPaused = false;',
    'volatile bool skyroverSpeakerPlaying = false;',
    '',
    'void skyroverSpeakerSetVolume(int percent) {',
    '  if (percent < 0) percent = 0;',
    '  if (percent > 100) percent = 100;',
    '  skyroverSpeakerVolume = percent;',
    '}',
    '',
    'void skyroverSpeakerPause() {',
    '  skyroverSpeakerPaused = true;',
    '}',
    '',
    'void skyroverSpeakerResume() {',
    '  skyroverSpeakerPaused = false;',
    '}',
    '',
    'void skyroverSpeakerStop() {',
    '  skyroverSpeakerStopRequested = true;',
    '  skyroverSpeakerPaused = false;',
    '}',
    '',
    'void skyroverSpeakerWriteTone(int freqHz, int durationMs) {',
    '  if (!skyroverI2sAudioReady || freqHz <= 0 || durationMs <= 0) {',
    '    return;',
    '  }',
    '  const int sampleRate = SKYROVER_I2S_SAMPLE_RATE;',
    '  const int totalSamples = (sampleRate * durationMs) / 1000;',
    '  const double amplitude = 12000.0 * ((double)skyroverSpeakerVolume / 100.0);',
    '  const double phaseStep = 2.0 * 3.141592653589793 * (double)freqHz / (double)sampleRate;',
    '  double phase = 0.0;',
    '  int16_t buffer[128];',
    '  int written = 0;',
    '  skyroverSpeakerPlaying = true;',
    '  skyroverSpeakerStopRequested = false;',
    '  while (written < totalSamples) {',
    '    if (skyroverSpeakerStopRequested) {',
    '      break;',
    '    }',
    '    while (skyroverSpeakerPaused && !skyroverSpeakerStopRequested) {',
    '      delay(5);',
    '    }',
    '    if (skyroverSpeakerStopRequested) {',
    '      break;',
    '    }',
    '    int chunk = totalSamples - written;',
    '    if (chunk > 128) chunk = 128;',
    '    for (int i = 0; i < chunk; i++) {',
    '      buffer[i] = (int16_t)(sin(phase) * amplitude);',
    '      phase += phaseStep;',
    '      if (phase > 2.0 * 3.141592653589793) {',
    '        phase -= 2.0 * 3.141592653589793;',
    '      }',
    '    }',
    '    size_t bytesWritten = 0;',
    '    i2s_write(SKYROVER_I2S_PORT, (const char*)buffer, chunk * sizeof(int16_t), &bytesWritten, portMAX_DELAY);',
    '    written += chunk;',
    '  }',
    '  skyroverSpeakerPlaying = false;',
    '}',
    '',
    'void skyroverSpeakerPlayPattern(const int* freqs, const int* durations, int count) {',
    '  skyroverSpeakerStopRequested = false;',
    '  skyroverSpeakerPaused = false;',
    '  for (int i = 0; i < count; i++) {',
    '    if (skyroverSpeakerStopRequested) {',
    '      break;',
    '    }',
    '    if (freqs[i] <= 0) {',
    '      delay(durations[i]);',
    '    } else {',
    '      skyroverSpeakerWriteTone(freqs[i], durations[i]);',
    '    }',
    '  }',
    '  i2s_zero_dma_buffer(SKYROVER_I2S_PORT);',
    '}',
    '',
    'void skyroverSpeakerPlaySound(const char* soundId) {',
    '  if (strcmp(soundId, "BELL") == 0) {',
    '    const int freqs[] = {1047, 1319, 1568};',
    '    const int durs[] = {180, 180, 350};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 3);',
    '  } else if (strcmp(soundId, "ALARM") == 0) {',
    '    const int freqs[] = {880, 0, 880, 0, 880, 0, 988};',
    '    const int durs[] = {160, 80, 160, 80, 160, 80, 400};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 7);',
    '  } else if (strcmp(soundId, "CLAP") == 0) {',
    '    const int freqs[] = {2000, 0, 1600};',
    '    const int durs[] = {40, 30, 60};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 3);',
    '  } else if (strcmp(soundId, "DOG") == 0) {',
    '    const int freqs[] = {400, 300, 450, 280};',
    '    const int durs[] = {120, 100, 120, 180};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 4);',
    '  } else if (strcmp(soundId, "CAT") == 0) {',
    '    const int freqs[] = {900, 1200, 800};',
    '    const int durs[] = {150, 180, 220};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 3);',
    '  } else if (strcmp(soundId, "DRUM") == 0) {',
    '    const int freqs[] = {150, 0, 120, 0, 180};',
    '    const int durs[] = {80, 60, 80, 60, 120};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 5);',
    '  } else {',
    '    // Custom — short pleasant tone',
    '    const int freqs[] = {523, 659, 784};',
    '    const int durs[] = {200, 200, 300};',
    '    skyroverSpeakerPlayPattern(freqs, durs, 3);',
    '  }',
    '}',
  ].join('\n');
}

function ensureSpeakerSupport(): void {
  ensureI2sAudioBus();
  arduinoGenerator.addInclude('#include <string.h>');
  arduinoGenerator.addGlobal(speakerHelperCode());
}

export function registerBeginnerSpeakerBlockGenerators(): void {
  arduinoGenerator.forBlock['beginner_speaker_play_sound'] = (block: Block) => {
    ensureSpeakerSupport();
    const sound = String(block.getFieldValue('SOUND') ?? 'BELL');
    return statementPrefix(block, `skyroverSpeakerPlaySound("${sound}");\n`);
  };

  arduinoGenerator.forBlock['beginner_speaker_pause'] = (block: Block) => {
    ensureSpeakerSupport();
    return statementPrefix(block, 'skyroverSpeakerPause();\n');
  };

  arduinoGenerator.forBlock['beginner_speaker_resume'] = (block: Block) => {
    ensureSpeakerSupport();
    return statementPrefix(block, 'skyroverSpeakerResume();\n');
  };

  arduinoGenerator.forBlock['beginner_speaker_stop'] = (block: Block) => {
    ensureSpeakerSupport();
    return statementPrefix(block, 'skyroverSpeakerStop();\n');
  };

  arduinoGenerator.forBlock['beginner_speaker_set_volume'] = (block: Block) => {
    ensureSpeakerSupport();
    const volume = clampVolume(block.getFieldValue('VOLUME'));
    return statementPrefix(block, `skyroverSpeakerSetVolume(${volume});\n`);
  };
}

registerBeginnerSpeakerBlockGenerators();
